/**
 * Progress tracking, mastery and spaced repetition.
 *
 * Rules used here (documented in the README so they can be tuned):
 *
 *   MASTERY  — based on the streak of correct FIRST answers.
 *     3 in a row                       → "familiar"
 *     5 in a row on 2 different days   → "learned"
 *     any wrong answer                 → streak back to 0, learned drops to familiar
 *
 *   PRIORITY — based on how often the question has been answered wrongly.
 *     wrong 3+ times → high     wrong twice → medium
 *     wrong once     → review   learned     → low
 *
 *   SCHEDULING — a Leitner ladder. A correct answer moves the question one box
 *   further away in time, a wrong answer sends it back to box 0 (due now).
 */
import type {
  CategoryId,
  MasteryStatus,
  PracticePriority,
  ProgressState,
  Question,
  QuestionProgress,
  RoadSign,
  SessionRecord,
} from '../types';
import { CATEGORIES } from '../data/categories';

export const PROGRESS_SCHEMA_VERSION = 1;

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Box 0 is "due right away". */
export const REVIEW_INTERVALS_MS = [0, 10 * MINUTE, 4 * HOUR, DAY, 3 * DAY, 7 * DAY, 21 * DAY];

export const FAMILIAR_STREAK = 3;
export const LEARNED_STREAK = 5;
export const LEARNED_MIN_DAYS = 2;
const MAX_SESSIONS_KEPT = 200;
const MAX_CORRECT_DAYS_KEPT = 30;

export function todayKey(at: number = Date.now()): string {
  const d = new Date(at);
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function createEmptyProgress(): ProgressState {
  const now = Date.now();
  return {
    schemaVersion: PROGRESS_SCHEMA_VERSION,
    createdAt: now,
    updatedAt: now,
    questions: {},
    signs: {},
    sessions: [],
    testsTaken: 0,
    testsPassed: 0,
    studyTimeMs: 0,
  };
}

export function emptyQuestionProgress(id: string): QuestionProgress {
  return {
    id,
    seen: 0,
    correct: 0,
    wrong: 0,
    streak: 0,
    status: 'new',
    lastSeenAt: null,
    lastResult: null,
    box: 0,
    dueAt: 0,
    correctDays: [],
  };
}

export function getEntry(state: ProgressState, kind: 'question' | 'sign', id: string): QuestionProgress {
  const table = kind === 'sign' ? state.signs : state.questions;
  return table[id] ?? emptyQuestionProgress(id);
}

function statusFor(entry: QuestionProgress): MasteryStatus {
  if (entry.streak >= LEARNED_STREAK && entry.correctDays.length >= LEARNED_MIN_DAYS) return 'learned';
  if (entry.streak >= FAMILIAR_STREAK) return 'familiar';
  if (entry.seen > 0) return 'learning';
  return 'new';
}

export function priorityFor(entry: QuestionProgress): PracticePriority {
  if (entry.status === 'learned') return 'low';
  if (entry.wrong >= 3) return 'high';
  if (entry.wrong === 2) return 'medium';
  if (entry.wrong === 1) return 'review';
  return 'none';
}

export function needsPractice(entry: QuestionProgress): boolean {
  const p = priorityFor(entry);
  return p === 'high' || p === 'medium' || p === 'review';
}

/**
 * Records one answer. `correct` must be the result of the FIRST attempt on this
 * showing of the question — retries inside Learning Mode do not change the score.
 */
export function recordAnswer(
  state: ProgressState,
  kind: 'question' | 'sign',
  id: string,
  correct: boolean,
  at: number = Date.now(),
): ProgressState {
  const prev = getEntry(state, kind, id);
  const next: QuestionProgress = { ...prev, correctDays: [...prev.correctDays] };

  next.seen += 1;
  next.lastSeenAt = at;
  next.lastResult = correct ? 'correct' : 'wrong';

  if (correct) {
    next.correct += 1;
    next.streak += 1;
    const day = todayKey(at);
    if (!next.correctDays.includes(day)) {
      next.correctDays.push(day);
      if (next.correctDays.length > MAX_CORRECT_DAYS_KEPT) next.correctDays.shift();
    }
    next.box = Math.min(next.box + 1, REVIEW_INTERVALS_MS.length - 1);
  } else {
    next.wrong += 1;
    next.streak = 0;
    next.box = 0;
  }

  next.dueAt = at + REVIEW_INTERVALS_MS[next.box];
  next.status = statusFor(next);

  const table = kind === 'sign' ? { ...state.signs } : { ...state.questions };
  table[id] = next;

  return {
    ...state,
    updatedAt: at,
    questions: kind === 'sign' ? state.questions : table,
    signs: kind === 'sign' ? table : state.signs,
  };
}

/** Used by "Learn Signs" flash cards, where the learner marks a card themselves. */
export function markSignReviewed(state: ProgressState, signId: string, learned: boolean): ProgressState {
  return recordAnswer(state, 'sign', signId, learned);
}

export function appendSession(state: ProgressState, session: SessionRecord): ProgressState {
  const sessions = [session, ...state.sessions].slice(0, MAX_SESSIONS_KEPT);
  const duration = Math.max(0, session.endedAt - session.startedAt);
  return {
    ...state,
    sessions,
    updatedAt: Date.now(),
    // Ignore absurd durations (tab left open overnight) so "study time" stays useful.
    studyTimeMs: state.studyTimeMs + (duration < 3 * HOUR ? duration : 0),
    // Only a test that was carried through to the score screen counts.
    testsTaken: state.testsTaken + (session.mode === 'test' && typeof session.score === 'number' ? 1 : 0),
    testsPassed: state.testsPassed + (session.mode === 'test' && session.passed ? 1 : 0),
  };
}

/* ------------------------------------------------------------------ */
/* Statistics                                                          */
/* ------------------------------------------------------------------ */

export interface CategoryStat {
  id: CategoryId;
  total: number;
  seen: number;
  learned: number;
  needsPractice: number;
  correct: number;
  wrong: number;
  accuracy: number | null;
}

export interface OverallStats {
  totalQuestions: number;
  seen: number;
  learned: number;
  needsPractice: number;
  totalSigns: number;
  signsLearned: number;
  testsTaken: number;
  testsPassed: number;
  studyTimeMs: number;
  totalCorrect: number;
  totalWrong: number;
  accuracy: number | null;
  categories: CategoryStat[];
}

export function computeStats(
  state: ProgressState,
  questions: Question[],
  signs: RoadSign[],
): OverallStats {
  const categories = new Map<CategoryId, CategoryStat>();
  for (const c of CATEGORIES) {
    categories.set(c.id, {
      id: c.id,
      total: 0,
      seen: 0,
      learned: 0,
      needsPractice: 0,
      correct: 0,
      wrong: 0,
      accuracy: null,
    });
  }

  let seen = 0;
  let learned = 0;
  let needing = 0;
  let totalCorrect = 0;
  let totalWrong = 0;

  for (const q of questions) {
    const stat = categories.get(q.category);
    const entry = state.questions[q.id];
    if (stat) stat.total += 1;
    if (!entry) continue;

    if (entry.seen > 0) {
      seen += 1;
      if (stat) stat.seen += 1;
    }
    if (entry.status === 'learned') {
      learned += 1;
      if (stat) stat.learned += 1;
    }
    if (needsPractice(entry)) {
      needing += 1;
      if (stat) stat.needsPractice += 1;
    }
    totalCorrect += entry.correct;
    totalWrong += entry.wrong;
    if (stat) {
      stat.correct += entry.correct;
      stat.wrong += entry.wrong;
    }
  }

  for (const stat of categories.values()) {
    const attempts = stat.correct + stat.wrong;
    stat.accuracy = attempts > 0 ? stat.correct / attempts : null;
  }

  let signsLearned = 0;
  for (const s of signs) {
    const entry = state.signs[s.id];
    if (entry && entry.status === 'learned') signsLearned += 1;
  }

  const attempts = totalCorrect + totalWrong;
  return {
    totalQuestions: questions.length,
    seen,
    learned,
    needsPractice: needing,
    totalSigns: signs.length,
    signsLearned,
    testsTaken: state.testsTaken,
    testsPassed: state.testsPassed,
    studyTimeMs: state.studyTimeMs,
    totalCorrect,
    totalWrong,
    accuracy: attempts > 0 ? totalCorrect / attempts : null,
    categories: CATEGORIES.map((c) => categories.get(c.id)!),
  };
}

/* ------------------------------------------------------------------ */
/* Import / export                                                     */
/* ------------------------------------------------------------------ */

export interface ProgressExport {
  app: 'ohio-permit-arabic';
  exportedAt: string;
  progress: ProgressState;
}

export function buildExport(state: ProgressState): ProgressExport {
  return {
    app: 'ohio-permit-arabic',
    exportedAt: new Date().toISOString(),
    progress: state,
  };
}

function sanitizeEntry(raw: unknown, id: string): QuestionProgress | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const e = raw as Partial<QuestionProgress>;
  const num = (v: unknown, d = 0) => (typeof v === 'number' && Number.isFinite(v) ? v : d);
  const entry: QuestionProgress = {
    id,
    seen: Math.max(0, Math.floor(num(e.seen))),
    correct: Math.max(0, Math.floor(num(e.correct))),
    wrong: Math.max(0, Math.floor(num(e.wrong))),
    streak: Math.max(0, Math.floor(num(e.streak))),
    status: 'new',
    lastSeenAt: typeof e.lastSeenAt === 'number' ? e.lastSeenAt : null,
    lastResult: e.lastResult === 'correct' || e.lastResult === 'wrong' ? e.lastResult : null,
    box: Math.min(Math.max(0, Math.floor(num(e.box))), REVIEW_INTERVALS_MS.length - 1),
    dueAt: num(e.dueAt),
    correctDays: Array.isArray(e.correctDays)
      ? e.correctDays.filter((d): d is string => typeof d === 'string').slice(-MAX_CORRECT_DAYS_KEPT)
      : [],
  };
  entry.status = statusFor(entry);
  return entry;
}

/** Validates an imported file. Returns null when the file is not ours. */
export function parseImportedProgress(raw: unknown): ProgressState | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const outer = raw as Record<string, unknown>;
  const candidate = (outer.progress ?? outer) as Record<string, unknown>;
  if (typeof candidate !== 'object' || candidate === null) return null;
  if (typeof candidate.questions !== 'object' || candidate.questions === null) return null;

  const state = createEmptyProgress();
  state.createdAt = typeof candidate.createdAt === 'number' ? candidate.createdAt : state.createdAt;
  state.testsTaken = typeof candidate.testsTaken === 'number' ? Math.max(0, candidate.testsTaken) : 0;
  state.testsPassed = typeof candidate.testsPassed === 'number' ? Math.max(0, candidate.testsPassed) : 0;
  state.studyTimeMs = typeof candidate.studyTimeMs === 'number' ? Math.max(0, candidate.studyTimeMs) : 0;

  for (const [id, value] of Object.entries(candidate.questions as Record<string, unknown>)) {
    const entry = sanitizeEntry(value, id);
    if (entry) state.questions[id] = entry;
  }
  if (typeof candidate.signs === 'object' && candidate.signs !== null) {
    for (const [id, value] of Object.entries(candidate.signs as Record<string, unknown>)) {
      const entry = sanitizeEntry(value, id);
      if (entry) state.signs[id] = entry;
    }
  }
  if (Array.isArray(candidate.sessions)) {
    state.sessions = candidate.sessions
      .filter((s): s is SessionRecord => typeof s === 'object' && s !== null)
      .slice(0, MAX_SESSIONS_KEPT);
  }
  return state;
}

export function formatDuration(ms: number, lang: 'ar' | 'en'): string {
  const totalMinutes = Math.round(ms / MINUTE);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (lang === 'ar') {
    if (hours === 0) return `${minutes} دقيقة`;
    return `${hours} ساعة و${minutes} دقيقة`;
  }
  if (hours === 0) return `${minutes} min`;
  return `${hours} h ${minutes} min`;
}
